import { useState } from 'react'
import { ArrowUpRight, ImageOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { LocalizedLink } from '@/components/LocalizedLink'
import type { SupportedLanguage } from '@/i18n'
import { getNewsTranslationKey, localizedNewsRecord, type NewsArticle, type NewsCopy } from '@/data/news'

/** One editorial card for the archive and the three latest stories on Home. */
export function NewsCard({ article, language, onOpen, headingLevel = 'h2' }: {
  article: NewsArticle
  language: SupportedLanguage
  onOpen?: (article: NewsArticle, trigger: HTMLElement) => void
  headingLevel?: 'h2' | 'h3'
}) {
  const { t } = useTranslation('common')
  const record = localizedNewsRecord(article, language)
  const copy = t(getNewsTranslationKey(article.id), { returnObjects: true }) as unknown as NewsCopy
  const [coverFailed, setCoverFailed] = useState(false)
  const sourceFallback = copy.sourceLanguage && copy.sourceLanguage !== language
  const Heading = headingLevel
  const content = <>
    <div className="news-card-cover">
      {record?.primaryMedia && !coverFailed
        ? <img className={article.preservePrimaryMedia ? 'news-media-preserve' : undefined} src={record.primaryMedia} alt={copy.mediaAlts[0] ?? copy.title} loading="lazy" onError={() => setCoverFailed(true)} />
        : <span className="news-card-no-cover"><ImageOff aria-hidden="true" /><span>{t('news.noCover')}</span></span>}
    </div>
    <div className="news-card-copy">
      <p className="news-card-meta"><span>{t('news.categoryNews')}</span><time dateTime={record?.sourceDate ?? undefined}>{record?.sourceDate ? new Intl.DateTimeFormat(language, { dateStyle: 'medium' }).format(new Date(record.sourceDate)) : ''}</time></p>
      {sourceFallback && <p className="news-source-note">{t('news.sourceFallback')}</p>}
      <Heading>{copy.title}</Heading>
      <p className="news-card-excerpt">{copy.excerpt}</p>
      <span className="news-card-action">{t('news.readArticle')}<ArrowUpRight aria-hidden="true" /></span>
    </div>
  </>
  return <article className="news-card" lang={sourceFallback ? copy.sourceLanguage : undefined}>
    {onOpen
      ? <button type="button" className="news-card-button" onClick={event => onOpen(article, event.currentTarget)} aria-label={t('news.readNamed', { title: copy.title })}>{content}</button>
      : <LocalizedLink to={article.id} className="news-card-button" aria-label={t('news.readNamed', { title: copy.title })}>{content}</LocalizedLink>}
  </article>
}
